import React, { Component } from 'react'
import Swal from 'sweetalert2'
import { AiOutlineReload, AiOutlineSearch, AiOutlineCheckCircle, AiOutlineCloseCircle } from "react-icons/ai"
import { getInformationWithDataV3, create_Delete_Update_InformationV3 } from '../../services/CABE'
import { getValueCookie } from '../../services/cookieService'
import "../../css/general-style.css"
import s from '../../css/SOMissingTool.module.css'
import { Menu } from "../../components/Menu/Menu"

const SUPPORTED_COMPANIES = [3, 6] // Nebraska (7) excluded for now — same as soMissingTool.ts
const VERIFY_INTERVAL_MS = 5000
const VERIFY_MAX_TRIES = 8 // ~40s — the queue/worker can take a while to land the order in Sage

const money = (n) => `$${Number(n || 0).toFixed(2)}`
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// No optional chaining / nullish coalescing in this project's CRA version
// (react-scripts 2.1.3 — crashes the compiler).
const errorMessageOf = (response) => {
    if (response && response.message) return response.message
    if (response && response.error) return response.error
    return ''
}

const isOk = (response) => !!(response && response.status && response.status.code === 1)

/**
 * Tools → SO Missing in Sage (2026-11-02).
 *
 * Sales Orders saved in the portal in the last 10 days (create_csv, latest
 * version per order) that are not in Sage — not an open SO, not an open
 * invoice, not in invoice history. Each one can be re-sent like a "Save and
 * Print" retry: the backend hands back the body saved in create_csv (same
 * username), it goes to /sales/set/order, and /sales/get/header/by-no
 * confirms it landed. One order at a time, never in parallel.
 *
 * Admin-only (backend re-checks userCompany.idTypeofUser on every call — this
 * TypeUser check only avoids showing a broken page). See soMissingTool.ts.
 */
export class SOMissingTool extends Component {
    constructor(props) {
        super(props)
        this.state = {
            isAdmin: getValueCookie('TypeUser') === '1',
            idCompany: Number(getValueCookie('CompanyId')),
            loading: false,
            loadError: '',
            orders: [],
            meta: null,
            detailOrder: null,
            resendingNo: '',  // NoOrder being re-sent right now ('' = none) — one at a time
            results: {},      // noOrder -> { state: 'sending'|'verifying'|'ok'|'error', message }
        }
    }

    componentDidMount() {
        if (this.state.isAdmin && SUPPORTED_COMPANIES.includes(this.state.idCompany)) {
            this.load()
        }
    }

    componentWillUnmount() {
        this.unmounted = true
    }

    load = async () => {
        this.setState({ loading: true, loadError: '' })
        try {
            const response = await getInformationWithDataV3('/v3/tools/so-missing/list', { idCompany: this.state.idCompany })
            if (isOk(response)) {
                this.setState({ orders: response.data || [], meta: response.meta || null, loading: false, results: {} })
            } else {
                const error = errorMessageOf(response)
                const message = error === 'SAGE_UNAVAILABLE'
                    ? 'Sage did not answer. Try again in a moment.'
                    : (error || 'Could not load the list.')
                this.setState({ loading: false, loadError: message })
            }
        } catch (e) {
            this.setState({ loading: false, loadError: 'Could not reach the server.' })
        }
    }

    setResult = (noOrder, result) => {
        if (this.unmounted) return
        this.setState((st) => ({ results: { ...st.results, [noOrder]: result } }))
    }

    /** Polls /sales/get/header/by-no until the order exists (and is this exact order) or we give up. */
    verifyInSage = async (noOrder) => {
        for (let i = 0; i < VERIFY_MAX_TRIES; i++) {
            if (this.unmounted) return false
            try {
                const response = await getInformationWithDataV3('/sales/get/header/by-no', { SalesOrderNo: noOrder, idCompany: this.state.idCompany })
                const rows = response && Array.isArray(response.data) ? response.data : []
                if (rows.some((row) => String(row.SalesOrderNo || '').trim() === noOrder)) return true
            } catch (e) { /* keep trying until the last attempt */ }
            await wait(VERIFY_INTERVAL_MS)
        }
        return false
    }

    resend = async (order) => {
        if (this.state.resendingNo) return
        const noOrder = String(order.noOrder).trim()

        const confirmation = await Swal.fire({
            title: `Re-send ${noOrder}?`,
            html: `${order.customerName} (${order.customerNo})<br/>${money(order.total)} — ${order.lines} lines<br/>Saved by <b>${order.userName}</b>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#2563EB',
            cancelButtonColor: '#64748B',
            confirmButtonText: 'Yes, re-send',
            cancelButtonText: 'Cancel',
        })
        if (!confirmation.isConfirmed) return

        this.setState({ resendingNo: noOrder })
        this.setResult(noOrder, { state: 'sending', message: 'Sending to Sage...' })
        try {
            const bodyResponse = await getInformationWithDataV3('/v3/tools/so-missing/resend-body', { idCompany: this.state.idCompany, noOrder })
            if (!isOk(bodyResponse) || !bodyResponse.data || !bodyResponse.data.body) {
                this.setResult(noOrder, { state: 'error', message: errorMessageOf(bodyResponse) || 'Could not read the saved order.' })
                return
            }

            const sendResponse = await create_Delete_Update_InformationV3('/sales/set/order', bodyResponse.data.body)
            if (!isOk(sendResponse)) {
                this.setResult(noOrder, { state: 'error', message: errorMessageOf(sendResponse) || 'The server rejected the order.' })
                return
            }

            this.setResult(noOrder, { state: 'verifying', message: 'Sent — checking in Sage (up to a minute)...' })
            const found = await this.verifyInSage(noOrder)
            if (found) {
                this.setResult(noOrder, { state: 'ok', message: `${noOrder} is now in Sage.` })
                Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: `${noOrder} is now in Sage`, showConfirmButton: false, timer: 3000 })
            } else {
                this.setResult(noOrder, { state: 'error', message: `${noOrder} was sent but is not in Sage yet. Check the Queue Monitor or try again later.` })
            }
        } catch (e) {
            this.setResult(noOrder, { state: 'error', message: 'Could not reach the server.' })
        } finally {
            if (!this.unmounted) this.setState({ resendingNo: '' })
        }
    }

    renderBadge(order) {
        if (!order.monitorStatus) return null
        let className = s.badge
        if (order.monitorStatus === 'failed') className = `${s.badge} ${s.badgeFailed}`
        if (order.monitorStatus === 'confirmed') className = `${s.badge} ${s.badgeConfirmed}`
        return <span className={className} title={order.monitorError || ''}>Monitor: {order.monitorStatus}</span>
    }

    renderResult(result) {
        if (!result) return null
        if (result.state === 'ok') {
            return <div className={`${s.result} ${s.resultOk}`}><AiOutlineCheckCircle /> {result.message}</div>
        }
        if (result.state === 'error') {
            return <div className={`${s.result} ${s.resultError}`}><AiOutlineCloseCircle /> {result.message}</div>
        }
        return (
            <div className={`${s.result} ${s.resultPending}`}>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> {result.message}
            </div>
        )
    }

    renderOrder(order) {
        const { resendingNo, results } = this.state
        const noOrder = String(order.noOrder).trim()
        const result = results[noOrder]
        const done = !!(result && result.state === 'ok')
        const busy = resendingNo === noOrder

        return (
            <div key={order.createCsvId} className={done ? `${s.card} ${s.cardDone}` : s.card}>
                <div className={s.info}>
                    <div className={s.orderNo}>{noOrder}{this.renderBadge(order)}</div>
                    <div className={s.customer}>{order.customerName} <span className={s.meta}>({order.customerNo})</span></div>
                    <div className={s.meta}>Date {order.date} · {order.lines} lines · by {order.userName}</div>
                    <div className={s.total}>{money(order.total)}</div>
                </div>
                <div className={s.actions}>
                    <button type="button" className={s.iconBtn} onClick={() => this.setState({ detailOrder: order })} aria-label={`View detail of ${noOrder}`}>
                        <AiOutlineSearch /> <span>Detail</span>
                    </button>
                    <button type="button" className={`${s.iconBtn} ${s.iconBtnPrimary}`} disabled={!!resendingNo || done} onClick={() => this.resend(order)} aria-label={`Re-send ${noOrder}`}>
                        <AiOutlineReload /> <span>{busy ? 'Sending...' : 'Re-send'}</span>
                    </button>
                </div>
                {this.renderResult(result)}
            </div>
        )
    }

    renderDetailModal() {
        const order = this.state.detailOrder
        if (!order) return null
        const detail = order.object && Array.isArray(order.object.Detail) ? order.object.Detail : []
        const items = detail.filter((item) => item.Visible !== 0)
        const close = () => this.setState({ detailOrder: null })

        return (
            <div className={s.overlay} onClick={close} role="presentation">
                <div className={s.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="so-missing-detail-title">
                    <div className={s.modalHeader}>
                        <div>
                            <h2 id="so-missing-detail-title" className={s.modalTitle}>{String(order.noOrder).trim()}</h2>
                            <div className={s.customer}>{order.customerName} ({order.customerNo})</div>
                            <div className={s.meta}>Date {order.date} · by {order.userName}</div>
                        </div>
                        <button type="button" className={s.closeBtn} onClick={close} aria-label="Close">×</button>
                    </div>
                    <div className={s.modalBody}>
                        {items.length === 0 && <div className={s.meta}>This order has no items.</div>}
                        {items.map((item, index) => (
                            <div key={`${item.itemCode}-${index}`} className={s.item}>
                                <div className={s.itemDesc}>
                                    {item.abbreviatedDesc || item.itemCode}
                                    <div className={s.itemCode}>{item.itemCode}</div>
                                </div>
                                <div className={s.itemNumbers}>
                                    {Number(item.quantityOrdered || 0)} × {money(item.UnitPrice)}
                                    <div><strong>{money(item.totalCost)}</strong></div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className={s.modalFooter}>
                        <span>Total</span>
                        <span>{money(order.total)}</span>
                    </div>
                </div>
            </div>
        )
    }

    renderContent() {
        const { isAdmin, idCompany, loading, loadError, orders, meta } = this.state

        if (!isAdmin) {
            return <div className={s.notice}>This tool is only available to administrators.</div>
        }
        if (!SUPPORTED_COMPANIES.includes(idCompany)) {
            return <div className={s.notice}>This tool is not available for this company yet.</div>
        }

        return (
            <div>
                {loading && (
                    <div className={s.notice}>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        Comparing the last 10 days against Sage...
                    </div>
                )}
                {!loading && loadError && (
                    <div className={`${s.notice} ${s.noticeError}`}><AiOutlineCloseCircle /> {loadError}</div>
                )}
                {!loading && !loadError && meta && (
                    <div className={s.summary}>{orders.length} missing of {meta.scanned} orders saved in the last {meta.lookbackDays} days.</div>
                )}
                {!loading && !loadError && orders.length === 0 && (
                    <div className={`${s.notice} ${s.noticeOk}`}><AiOutlineCheckCircle /> Every order saved in the last 10 days is in Sage.</div>
                )}
                {!loading && !loadError && orders.map((order) => this.renderOrder(order))}
            </div>
        )
    }

    render() {
        const { loading, resendingNo, isAdmin, idCompany } = this.state
        const canLoad = isAdmin && SUPPORTED_COMPANIES.includes(idCompany)

        return (
            <div className={s.page}>
                <Menu />
                <div className={s.hero}>
                    <div>
                        <h1 className={s.title}>SO Missing in Sage</h1>
                        <p className={s.subtitle}>Sales orders saved in the last 10 days that never reached Sage. Re-send them one at a time.</p>
                    </div>
                    {canLoad && (
                        <button type="button" className={s.secondaryBtn} disabled={loading || !!resendingNo} onClick={this.load}>
                            <AiOutlineReload /> {loading ? 'Loading...' : 'Refresh'}
                        </button>
                    )}
                </div>
                <div className={s.content}>
                    {this.renderContent()}
                </div>
                {this.renderDetailModal()}
            </div>
        )
    }
}

export default SOMissingTool
